<template>
  <f-modal
    :class="{ 'is-visible': isVisible }"
    @close="handleClose">
    <f-modal-header>
      <h3 class="heading-medium">
        <template v-if="editMode">
          {{ $t('adviserSettingsEducationInfo.editEntry') }}
        </template>
        <template v-else>
          {{ $t('adviserSettingsEducationInfo.newEntry') }}
        </template>
      </h3>
    </f-modal-header>

    <f-modal-body>
      <validation-observer
        ref="adviser-education-popup-form"
        tag="form">

        <div class="form-group">
          <label for="study-field">
            {{ $t('adviserSettingsEducationInfo.fieldOfStudy') }}
          </label>

          <validation-provider
            rules="required"
            name="study-field"
            v-slot="{ errors }">
            <f-input
              v-model="data.study_field"
              :state="errors.length ? 'invalid' : null"/>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div class="form-group">
          <label for="school-name">
            {{ $t('adviserSettingsEducationInfo.university') }}
          </label>

          <validation-provider
            rules="required"
            name="school-name"
            v-slot="{ errors }">
            <f-input
              v-model="data.school_name"
              :state="errors.length ? 'invalid' : null"/>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div class="form-group">
          <label for="start-at">{{ $t('adviserSettingsEducationInfo.timeStart') }}</label>
          <validation-provider
            rules="required"
            name="start-at"
            v-slot="{ errors }">
            <div
              :class="{
                'is-date-range': true,
                'vs-wrap-text': true,
                'is-invalid': errors.length
              }">
              <v-date-picker
                v-model="data.start_at"
                mode="date"
                :locale="$i18n.locale"
                class="date"
                :max-date='data.end_at'>
                <template v-slot="{ inputValue, togglePopover }">
                  <input
                    :value="inputValue"
                    @click="togglePopover()"
                    class="form-control f-input" />
                </template>
              </v-date-picker>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </div>
          </validation-provider>
        </div>

        <div class="form-group">
          <label for="end-at">{{ $t('adviserSettingsEducationInfo.timeEnd') }}</label>
          <validation-provider
            name="end-at"
            v-slot="{ errors }">
            <div
              :class="{
                'is-date-range': true,
                'vs-wrap-text': true,
                'is-invalid': errors.length
              }">
              <v-date-picker
                v-model="data.end_at"
                mode="date"
                :locale="$i18n.locale"
                class="date"
                :min-date='data.start_at'>
                <template v-slot="{ inputValue, togglePopover }">
                  <input
                    :value="inputValue"
                    @click="togglePopover()"
                    class="form-control f-input" />
                </template>
              </v-date-picker>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </div>
          </validation-provider>
        </div>

      </validation-observer>

    </f-modal-body>
    <f-modal-footer>
      <div class="flex flex-justify-content-between">
        <f-button
          class="ml-0"
          theme="danger"
          @click.prevent="handleClose">
          {{ $t('ui.cancel') }}
        </f-button>
        <f-button
          class="ml-auto"
          theme="primary"
          :disabled="submitInProgress"
          @click.prevent="validateForm">
          <div
            v-if="submitInProgress"
            class="loading-spinner"
            role="status">
          </div>
          <template v-if="editMode">{{ $t('ui.saveChange') }}</template>
          <template v-else>{{ $t('ui.add') }}</template>
        </f-button>
      </div>
    </f-modal-footer>
  </f-modal>
</template>
<script>
import FormUtils from '@/utils/FormUtils.js';

export default {
  name: 'adviser-education-popup',
  computed: {
  },
  data() {
    return {
      data: {
        id: 0,
        study_field: null,
        school_name: null,
        start_at: null,
        end_at: null,
      },
      editMode: false,
      isHidden: true,
      submitInProgress: false,
      isVisible: false
    };
  },
  mounted() {
    this.$bus.$on('adviser-education-popup-show', this.openPopup);
  },
  methods: {
    handleClose() {
      document.body.classList.remove('no-scroll');
      this.isVisible = false;
    },
    openPopup(education = null) {
      this.editMode = education !== null;

      if (this.editMode) {
        this.data.id = education.id;
        this.data.study_field = education.study_field;
        this.data.school_name = education.school_name;
        this.data.start_at = education.start_at;
        this.data.end_at = education.end_at;
      } else {
        this.data.id = 0;
        this.data.study_field = null;
        this.data.school_name = null;
        this.data.start_at = null;
        this.data.end_at = null;
      }

      setTimeout(() => {
        this.isVisible = true;
        this.submitInProgress = false;
        document.body.classList.add('no-scroll');
        if (this.$refs['adviser-education-popup-form']) {
          this.$refs['adviser-education-popup-form'].reset();
        }
      }, 0);
    },
    validateForm () {
      FormUtils.validate(this.$refs['adviser-education-popup-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = this.prepareFormDataToSend();
      let endpointUrl = this.editMode ? '/api/adviser/adviser/educations/update/' + this.data.id : '/api/adviser/adviser/educations/store';

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: this.$t('adviserSettingsEducationInfo.savedEntry'),
        successText: this.$t('adviserSettingsEducationInfo.selectedEntryHasBeenSaved'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('adviserSettingsEducationInfo.entryModifyError'),
        successAction: () => {
          this.$bus.$emit('advisor-education-reload');
          this.handleClose();
        }
      });
    },
    prepareFormDataToSend () {
      let formDataToSend = {
        study_field: this.data.study_field,
        school_name: this.data.school_name,
        start_at: this.prepareDateString(this.data.start_at),
        end_at: this.prepareDateString(this.data.end_at),
      };

      return formDataToSend;
    },
    prepareDateString (input) {
      if (input === null) {
        return null;
      }
      input = new Date(input);

      return [
        input.getFullYear(),
        input.getMonth() + 1,
        input.getDate()
      ]
        .map(n => n < 10 ? '0' + n : n)
        .join('-');
    },
  },
  beforeDestroy () {
    this.$bus.$off('adviser-education-popup-show', this.openPopup);
  }
}
</script>
